import { darken, lighten, useTheme } from '@mui/material';
import { useMemo } from 'react';
import FormErrorType from '@/types/form/FormErrorType';

export enum ErrorStylingType {
    BackgroundAndText = 1,
    Text = 2,
    Input = 3
}

export const ErrorClassMapping = {
    [FormErrorType.HelperText]: 'helper-text',
    [FormErrorType.Warning]: 'error-warning',
    [FormErrorType.Low]: 'error-low',
    [FormErrorType.Normal]: 'error-normal',
    [FormErrorType.High]: 'error-high',
    [FormErrorType.Critical]: 'error-critical'
};

const useErrorStyling = (type?: FormErrorType, stylingType?: ErrorStylingType) => {
    const theme = useTheme();

    const errorStyling = useMemo(() => {
        const getColor = theme.palette.mode === 'light' ? darken : lighten;
        const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;
        const getBorderColor = theme.palette.mode === 'light' ? lighten : darken;
        
        let styling = null;

        if (stylingType === ErrorStylingType.Text) {
            switch (type) {
                case FormErrorType.HelperText:
                    styling = {
                        color: theme.palette.text.secondary,
                        '&  *': {
                            color: theme.palette.text.secondary  + ' !important'
                        }
                    };
                    break;
                case FormErrorType.Warning:
                    styling = {
                        color: theme.palette.info.main,
                        '&  *': {
                            color: theme.palette.info.main  + ' !important'
                        }
                    };
                    break;
                case FormErrorType.Low:
                    styling = {
                        color: theme.palette.warning.main,
                        '&  *': {
                            color: theme.palette.warning.main  + ' !important'
                        }
                    };
                    break;
                case FormErrorType.Normal:
                    styling = {
                        color: theme.palette.error.main,
                        '&  *': {
                            color: theme.palette.error.main  + ' !important'
                        }
                    };
                    break;
                case FormErrorType.High:
                    styling = {
                        color: theme.palette.warning.main,
                        '&  *': {
                            color: theme.palette.warning.main  + ' !important'
                        }
                    };
                    break;
                case FormErrorType.Critical:
                    styling = {
                        color: theme.palette.error.main,
                        '&  *': {
                            color: theme.palette.error.main  + ' !important'
                        }
                    };
                    break;
            }
        }

        if (stylingType === ErrorStylingType.Input) {
            switch (type) {
                case FormErrorType.HelperText:
                    styling = {
                        color: theme.palette.text.secondary,
                        borderColor: theme.palette.text.secondary,
                        '&  *': {
                            color: theme.palette.text.secondary  + ' !important',
                            borderColor: theme.palette.text.secondary  + ' !important',
                        },
                        '& .Mui-error': {
                            color: theme.palette.text.secondary  + ' !important',
                            borderColor: theme.palette.text.secondary  + ' !important',
                        },
                        '& .Mui-error.MuiOutlinedInput-notchedOutline': {
                            color: theme.palette.text.secondary  + ' !important',
                            borderColor: theme.palette.text.secondary  + ' !important',
                        }
                    };
                    break;
                case FormErrorType.Warning:
                    styling = {
                        color: theme.palette.info.main,
                        borderColor: theme.palette.info.main,
                        '&  *': {
                            color: theme.palette.info.main  + ' !important',
                            borderColor: theme.palette.info.main  + ' !important',
                        },
                        '& .Mui-error': {
                            color: theme.palette.info.main  + ' !important',
                            borderColor: theme.palette.info.main  + ' !important',
                        },
                        '& .Mui-error.MuiOutlinedInput-notchedOutline': {
                            color: theme.palette.info.main  + ' !important',
                            borderColor: theme.palette.info.main  + ' !important',
                        }
                    };
                    break;
                case FormErrorType.Low:
                    styling = {
                        color: theme.palette.warning.main,
                        borderColor: theme.palette.warning.main,
                        '&  *': {
                            color: theme.palette.warning.main  + ' !important',
                            borderColor: theme.palette.warning.main  + ' !important',
                        },
                        '& .Mui-error': {
                            color: theme.palette.warning.main  + ' !important',
                            borderColor: theme.palette.warning.main  + ' !important',
                        },
                        '& .Mui-error.MuiOutlinedInput-notchedOutline': {
                            color: theme.palette.warning.main  + ' !important',
                            borderColor: theme.palette.warning.main  + ' !important',
                        }
                    };
                    break;
                case FormErrorType.Normal:
                    styling = {
                        color: theme.palette.error.main,
                        borderColor: theme.palette.error.main,
                        '&  *': {
                            color: theme.palette.error.main  + ' !important',
                            borderColor: theme.palette.error.main  + ' !important',
                        },
                        '& .Mui-error': {
                            color: theme.palette.error.main  + ' !important',
                            borderColor: theme.palette.error.main  + ' !important',
                        },
                        '& .Mui-error.MuiOutlinedInput-notchedOutline': {
                            color: theme.palette.error.main  + ' !important',
                            borderColor: theme.palette.error.main  + ' !important',
                        }
                    };
                    break;
                case FormErrorType.High:
                    styling = {
                        color: theme.palette.warning.main,
                        borderColor: theme.palette.warning.main,
                        '&  *': {
                            color: theme.palette.warning.main  + ' !important',
                            borderColor: theme.palette.warning.main  + ' !important',
                        },
                        '& .Mui-error': {
                            color: theme.palette.warning.main  + ' !important',
                            borderColor: theme.palette.warning.main  + ' !important',
                        },
                        '& .Mui-error.MuiOutlinedInput-notchedOutline': {
                            color: theme.palette.warning.main  + ' !important',
                            borderColor: theme.palette.warning.main  + ' !important',
                        },
                        '& input': {
                            color: theme.palette.warning.main  + ' !important',
                            fontWeight: theme.typography.fontWeightMedium,
                            backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),
                        },
                        '& .MuiSelect-select': {
                            color: theme.palette.warning.main  + ' !important',
                            fontWeight: theme.typography.fontWeightMedium,
                            backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),
                        }
                    };
                    break;
                case FormErrorType.Critical:
                    styling = {
                        color: theme.palette.error.main,
                        borderColor: theme.palette.error.main,
                        '&  *': {
                            color: theme.palette.error.main  + ' !important',
                            borderColor: theme.palette.error.main  + ' !important',
                        },
                        '& .Mui-error': {
                            color: theme.palette.error.main  + ' !important',
                            borderColor: theme.palette.error.main  + ' !important',
                        },
                        '& .Mui-error.MuiOutlinedInput-notchedOutline': {
                            color: theme.palette.error.main  + ' !important',
                            borderColor: theme.palette.error.main  + ' !important'
                        },
                        '& input': {
                            color: theme.palette.error.main  + ' !important',
                            fontWeight: theme.typography.fontWeightMedium,
                            backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
                        },
                        '& .MuiSelect-select': {
                            color: theme.palette.error.main  + ' !important',
                            fontWeight: theme.typography.fontWeightMedium,
                            backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
                        }
                    };
                    break;
            }

            if (styling || stylingType === ErrorStylingType.Input) {
                return {
                    ...styling,
                    '& .MuiFormHelperText-root': {
                        mx: '0 !important'
                    },
                    [`&  *.${ErrorClassMapping[FormErrorType.HelperText]}`]: {
                        display: 'block',
                        py: 0.5, 
                        px: 2,
                        color: theme.palette.text.secondary  + ' !important',
                    },
                    [`&  *.${ErrorClassMapping[FormErrorType.Warning]}`]: {
                        display: 'block',
                        py: 0.5, 
                        px: 2,
                        color: theme.palette.info.main  + ' !important',
                    },
                    [`&  *.${ErrorClassMapping[FormErrorType.Low]}`]: {
                        display: 'block',
                        py: 0.5, 
                        px: 2,
                        color: theme.palette.warning.main  + ' !important',
                    },
                    [`&  *.${ErrorClassMapping[FormErrorType.Normal]}`]: {
                        display: 'block',
                        py: 0.5, 
                        px: 2,
                        color: theme.palette.error.main  + ' !important',
                    },
                    [`&  *.${ErrorClassMapping[FormErrorType.High]}`]: {
                        display: 'block',
                        py: 0.5, 
                        px: 2,
                        color: theme.palette.warning.main  + ' !important',
                        backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),
                    },
                    [`&  *.${ErrorClassMapping[FormErrorType.Critical]}`]: {
                        display: 'block',
                        py: 0.5, 
                        px: 2,
                        color: theme.palette.error.main  + ' !important',
                        backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
                    }
                }
            }
        }

        if (!stylingType || stylingType === ErrorStylingType.BackgroundAndText) {
            switch (type) {
                case FormErrorType.HelperText:
                    styling = {
                        color: getColor(theme.palette.text.secondary, 0.6)  + ' !important',
                        backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
                        borderColor: getBorderColor(theme.palette.info.main, 0.8),
                        '&  *': {
                            color: theme.palette.text.secondary  + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.text.secondary  + ' !important',
                        },
                        '& .MuiBox-root': {
                            color: theme.palette.text.secondary  + ' !important',
                        }
                    }
                    break;
                case FormErrorType.Warning:
                    styling = {
                        color: getColor(theme.palette.info.main, 0.6)  + ' !important',
                        backgroundColor: getBackgroundColor(theme.palette.info.main, 0.9),
                        borderColor: getBorderColor(theme.palette.info.main, 0.8),
                        '&  *': {
                            color: theme.palette.info.main  + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.info.main  + ' !important',
                        },
                        '& .MuiBox-root': {
                            color: theme.palette.info.main  + ' !important',
                        }
                    }
                    break;
                case FormErrorType.Low:
                    styling = {
                        color: getColor(theme.palette.warning.main, 0.6)  + ' !important',
                        backgroundColor: getBackgroundColor(theme.palette.warning.main, 0.9),
                        borderColor: getBorderColor(theme.palette.warning.main, 0.8),
                        '&  *': {
                            color: theme.palette.warning.main  + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.warning.main  + ' !important',
                        },
                        '& .MuiBox-root': {
                            color: theme.palette.warning.main  + ' !important',
                        }
                    }
                    break;
                case FormErrorType.Normal:
                    styling = {
                        color: getColor(theme.palette.error.main, 0.6) + ' !important',
                        backgroundColor: getBackgroundColor(theme.palette.error.main, 0.9),
                        borderColor: getBorderColor(theme.palette.error.main, 0.8),
                        '&  *': {
                            color: theme.palette.error.main  + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.error.main  + ' !important',
                        },
                        '& .MuiBox-root': {
                            color: theme.palette.error.main  + ' !important',
                        }
                    };
                    break;
                case FormErrorType.High:
                    styling = {
                        color: theme.palette.common.white,
                        fontWeight: theme.typography.fontWeightMedium,
                        backgroundColor: theme.palette.warning.main,
                        borderColor: getColor(theme.palette.warning.main, 0.1),
                        '&  *': {
                            color: theme.palette.common.white  + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.common.white  + ' !important',
                        },
                        '& .MuiBox-root': {
                            color: theme.palette.common.white  + ' !important',
                        }
                    };
                    break;
                case FormErrorType.Critical:
                    styling = {
                        color: theme.palette.common.white + ' !important',
                        fontWeight: theme.typography.fontWeightMedium,
                        backgroundColor: theme.palette.error.main,
                        borderColor: getColor(theme.palette.error.main, 0.1),
                        '&  *': {
                            color: theme.palette.common.white  + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: theme.palette.common.white  + ' !important',
                        },
                        '& .MuiBox-root': {
                            color: theme.palette.common.white  + ' !important',
                        }
                    }
                    break;
            }
        }

        return {};

    }, [type, stylingType, theme.palette.mode])
    
    return errorStyling;
}

export default useErrorStyling;